export type BlogPost = {
	slug: string;
	title: string;
	publishedAt: string;
	excerpt: string;
	sections: Array<{
		heading: string;
		paragraphs: string[];
	}>;
};

export const BLOG_POSTS: BlogPost[] = [
	{
		slug: "introducing-text-formatter-api-quickstart",
		title: "Introducing Text Formatter API: Quickstart in 5 minutes",
		publishedAt: "2026-05-05",
		excerpt:
			"Learn how to go from first API key to your first successful formatting workflow in a few minutes.",
		sections: [
			{
				heading: "Why Text Formatter API",
				paragraphs: [
					"Text Formatter API was built for ecommerce teams that need clean, consistent product content at scale. Whether you are managing supplier feeds, marketplace imports, product detail pages, or category copy, the goal is simple: spend less time fixing messy text and publish faster with consistent quality.",
				],
			},
			{
				heading: "Start in minutes, not days",
				paragraphs: [
					"You do not need a complex setup to start. Create an API key, send your text to the formatting endpoint, and review the result in your own workflow. Most teams can get a first meaningful integration working in less than five minutes, then expand from one use case to many.",
				],
			},
			{
				heading: "What results to expect",
				paragraphs: [
					"The biggest win is consistency. Instead of each person formatting differently, your app gets predictable output every time. That means fewer editorial fixes, fewer avoidable mistakes, and a smoother experience for anyone reading your content.",
					"If you are ready to try it, visit [api.textformatter.pro](https://api.textformatter.pro) and run a first request from your preferred environment. Once that first call works, you have a solid foundation for automating text quality across your product.",
				],
			},
			{
				heading: "Don't forget to get your API key",
				paragraphs: [
					"You free API key is available in the [dashboard](https://www.textformatter.pro/dashboard).",
				],
			},
		],
	},
	{
		slug: "five-formatting-workflows-that-save-hours-every-week",
		title: "5 formatting workflows that save hours every week",
		publishedAt: "2026-05-05",
		excerpt:
			"Five practical ways teams use Text Formatter to reduce repetitive editing and ship cleaner content faster.",
		sections: [
			{
				heading: "Why these workflows matter",
				paragraphs: [
					"Most teams do not lose time on strategy. They lose time on repeated cleanup: spacing fixes, broken paragraphs, inconsistent structure, and rushed edits before publishing. These tasks feel small, but they add up quickly over a week.",
				],
			},
			{
				heading: "1) Imported content cleanup",
				paragraphs: [
					"The first workflow to automate is usually imported content. Supplier feeds, CSV uploads, and copied text from external systems often arrive in uneven shape. Running that text through a formatting step before publishing prevents hours of manual correction later.",
				],
			},
			{
				heading: "2) Editorial prep for CMS and blog drafts",
				paragraphs: [
					"Another high-impact workflow is editorial prep. Blog drafts, landing pages, help-center content, and marketing snippets become easier to review when structure is already clean. Editors can focus on message quality instead of fixing formatting issues line by line.",
				],
			},
			{
				heading: "3) Support and knowledge-base consistency",
				paragraphs: [
					"Support teams also benefit. Response templates, knowledge-base answers, and internal macros stay easier to read when formatting is consistent. This improves response quality and helps teams keep a reliable tone, even during busy periods.",
				],
			},
			{
				heading: "4) Better text after bulk uploads",
				paragraphs: [
					"Supplier or partner text often enters your product through bulk feeds. A formatting pass right after import helps prevent malformed content from reaching customers and reduces last-minute cleanup before launch.",
				],
			},
			{
				heading: "5) Cleaner user-submitted content",
				paragraphs: [
					"User-submitted text is a great candidate. Cleaning text before display improves readability across dashboards, reports, and shared documents. The result is a better end-user experience and less reactive cleanup by your internal team.",
				],
			},
			{
				heading: "Start small and scale with confidence",
				paragraphs: [
					"You do not need to automate everything at once. Start with the single workflow that creates the most repetitive work today. Once you see time savings there, it becomes much easier to expand to the next workflow with confidence.",
				],
			},
		],
	},
];
